import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {ProfileMain} from 'src/components/profile-tab-structure';
import {SaveWithReasonButton} from 'src/components/save-with-reason/save-with-reason-button';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {STATSIG_GATES} from 'src/contexts/statsig-contex';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {SendWelcomeEmailSection} from 'src/modules/profile/user/tabs/user-login/sections/send-welcome-email';
import {useUserMfaEnforced} from 'src/modules/profile/user/tabs/user-login/useUserMfaEnforced';
import {getUserAuthenticationInfo} from 'src/openapi/admin/apis/UserAuthenticationInfoApi';
import {useAppDispatch} from 'src/store/hooks';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';
import {useGateValue} from '@pb/kit-lib-statsig';

import {EmailDeliveryLog} from './email-history/email-delivery-log';
import {LoginActions} from './sections/login-actions';
import {useLoginActionAvailable} from './sections/login-actions/useLoginLinkAvailable';
import {NextLoginActions} from './sections/next-login-actions';
import {PasswordSharingWarning} from './sections/password-sharing-warning';
import {useUserLoginPreconditions} from './useUserLoginPreconditions';

import type {SetStateAction} from 'react';
import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';
import type {AccessRight} from 'src/hooks/access-rights/constants';


type Props = {
    userId: number,
    accountId: number,
};

export const UserLogin = ({userId, accountId}: Props) => {
    const [busy, setBusy] = useState<boolean>(false);
    const {canEditUser} = useAdminAccessRight();
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);
    const {userFieldChange, userSubmit} = useEventTracking();
    const apiDispatch = useAppDispatch();
    const isEmailPrimaryMfaEnabled = useGateValue(STATSIG_GATES.EMAIL_PRIMARY_MFA);

    const {isCurrentChunkChanged, setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'user-login-password',
        onProceed: () => {
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        },
    });

    const onFieldChange = (section: string) => (parameterName: string) => {
        userFieldChange({
            section,
            viewType: 'Existed',
            parameterName,
        });
    };
    const {
        isLoading: isUserLoginPreconditionsLoading,
        loadingError: userLoginPreconditionsLoadingError,
        isChanged: isUserLoginPreconditionsChanged,
        data: userLoginPreconditions,
        setData: setUserLoginPreconditions,
        saveChanges: saveUserLoginPreconditions,
        discardChanges: discardUserLoginPreconditions,
    } = useUserLoginPreconditions({userId});

    const {
        isLoading: isUserMfaEnforcedLoading,
        loadingError: userMfaLoadingError,
        isChanged: isUserMfaEnforcedChanged,
        data: {mfaEnforced: userMfaEnforced, emailPrimaryMfa: emailPrimaryFactor},
        setData: setMfaSettings,
        saveChanges: saveMfaEnforced,
        discardChanges: discardMfaEnforced,
        loadData: reloadMfaSettings,
    } = useUserMfaEnforced({userId, isEmailPrimaryMfaEnabled});

    const setUserMfaEnforced = useCallback((value: SetStateAction<boolean>) => {
        setMfaSettings(previous => {
            const mfaEnforced = typeof value === 'function' ? value(previous.mfaEnforced) : value;
            return {...previous, mfaEnforced, emailPrimaryMfa: mfaEnforced && previous.emailPrimaryMfa};
        });
    }, [setMfaSettings]);

    const setEmailPrimaryFactor = useCallback((value: SetStateAction<boolean>) => {
        setMfaSettings(previous => ({
            ...previous,
            emailPrimaryMfa: previous.mfaEnforced
                && (typeof value === 'function' ? value(previous.emailPrimaryMfa) : value),
        }));
    }, [setMfaSettings]);

    const {
        isLoading: isLoginLinkLoading,
        data: {canLogin: canUseLoginLink},
    } = useLoginActionAvailable({userId});

    useEffect(() => {
        setIsChunkChanged(
            isUserLoginPreconditionsChanged || isUserMfaEnforcedChanged,
        );
    }, [
        isUserLoginPreconditionsChanged,
        isUserMfaEnforcedChanged,
        setIsChunkChanged,
    ]);

    const saveChanges = useCallback(async (options?: SaveOptions) => {
        userSubmit({place: 'User Login & Password', submit: true});
        const calls = [];
        setBusy(true);
        try {
            if (isUserLoginPreconditionsChanged) {
                calls.push(await saveUserLoginPreconditions(options));
            }
            if (isUserMfaEnforcedChanged) {
                const error = await saveMfaEnforced(options);
                calls.push(error);
                if (!error) {
                    apiDispatch(getUserAuthenticationInfo({userId: +userId}));
                }
            }
            setBusy(false);

            const errors = [...new Set(calls.filter(Boolean))];

            if (errors.length) {
                setErrorMessage(errors.join(' '));
                return errors.join(' ');
            } else {
                proceed();
            }

        } finally {
            setBusy(false);
        }
    }, [
        isUserLoginPreconditionsChanged,
        saveUserLoginPreconditions,
        isUserMfaEnforcedChanged,
        saveMfaEnforced,
        apiDispatch,
        proceed,
        setErrorMessage,
        userSubmit,
        userId,
    ]);

    const discardChanges = useCallback(() => {
        userSubmit({place: 'User Login & Password', submit: false});
        discardUserLoginPreconditions();
        discardMfaEnforced();
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [
        discardUserLoginPreconditions,
        discardMfaEnforced,
        userSubmit,
        setSuccessMessage,
    ]);

    const canEdit = useMemo(() => canEditUser(accountId), [canEditUser, accountId]);

    if (userMfaLoadingError) {
        return (
            <Group direction="v">
                <ServerResponseHandler
                    sectionName="Login & Password"
                    serverError={userMfaLoadingError}
                    accessRight={ACCESS_RIGHTS.USER_SCREEN_VIEW as AccessRight}>
                    <div/>
                </ServerResponseHandler>
                <Button busy={isUserMfaEnforcedLoading} onClick={reloadMfaSettings}>
                    Retry loading MFA settings
                </Button>
            </Group>
        );
    }

    return (
        <ServerResponseHandler
            sectionName="Login & Password"
            serverError={userLoginPreconditionsLoadingError || userMfaLoadingError}
            accessRight={ACCESS_RIGHTS.USER_SCREEN_VIEW as AccessRight}>
            <ProfileMain
                header={(
                    <Item grow={1}>
                        <Group justify="space-between">
                            <Typography
                                fontWeight="semi-bold"
                                variant="heading-level-3">
                                Login & Password
                            </Typography>
                            {canEdit ? (
                                <Group>
                                    <Button
                                        variant="Type.SECONDARY"
                                        disabled={!isCurrentChunkChanged || busy || isUserMfaEnforcedLoading}
                                        onClick={discardChanges}>
                                        Discard Changes
                                    </Button>
                                    <SaveWithReasonButton
                                        busy={busy}
                                        disabled={!isCurrentChunkChanged || isUserMfaEnforcedLoading}
                                        onSave={saveChanges}
                                    />
                                </Group>
                            ) : null}
                        </Group>
                    </Item>
                )}
                footer={<EmailDeliveryLog userId={userId}/>}>
                <LoginActions
                    isLoading={isUserLoginPreconditionsLoading || isLoginLinkLoading}
                    userId={userId}
                    disabled={!canUseLoginLink}
                    isSsoMandatory={userLoginPreconditions.ssoMandatory}
                />
                <NextLoginActions {...{
                    isLoading: isUserLoginPreconditionsLoading || isUserMfaEnforcedLoading
                        || isLoginLinkLoading,
                    userLoginPreconditions,
                    userMfaEnforced,
                    setUserMfaEnforced,
                    isEmailPrimaryMfaEnabled,
                    emailPrimaryFactor,
                    setEmailPrimaryFactor,
                    onChange: setUserLoginPreconditions,
                    disabled: !canEdit || !canUseLoginLink,
                    onFieldChange: onFieldChange('Next Login Actions'),
                }}
                />
                <Group direction="h" gap="xxxl">
                    <Group justify="start">
                        <SendWelcomeEmailSection {...{
                            isLoading: isUserLoginPreconditionsLoading || isLoginLinkLoading,
                            userId,
                            disabled: !canUseLoginLink,
                            isSsoMandatory: userLoginPreconditions.ssoMandatory,
                            isSsoEnabled: userLoginPreconditions.ssoEnabled,
                            accountId,
                        }}
                        />
                    </Group>
                    <Group>
                        <PasswordSharingWarning {...{
                            isLoading: isUserLoginPreconditionsLoading,
                            userId,
                            userLoginPreconditions,
                            onChange: setUserLoginPreconditions,
                            disabled: !canEdit || !canUseLoginLink,
                            onFieldChange: onFieldChange('Password Sharing Warning'),
                        }}
                        />
                    </Group>
                </Group>
            </ProfileMain>
        </ServerResponseHandler>
    );
};
